import React from 'react';
import Navbar from '../../components/Navbar/Navbar';
import './PoliticaPrivacidad.css';

const PoliticaPrivacidad = () => {
    const lastUpdated = '1 de abril de 2026';

    return (
        <div className="pp-wrapper">
            <Navbar />

            {/* Hero */}
            <div className="pp-hero">
                <span className="pp-hero-icon">🔒</span>
                <h1>Política de Privacidad</h1>
                <p>Última actualización: {lastUpdated} · Aplicable a Footy Games (Android e iOS)</p>
            </div>

            <div className="pp-content">

                {/* Intro */}
                <div className="pp-intro">
                    <p>
                        Bienvenido a <strong>Footy Games</strong>. Esta política explica de forma clara y transparente
                        qué información recopilamos cuando usás nuestra app, cómo la utilizamos y con quién la
                        compartimos. Al descargar o usar Footy Games aceptás los términos descritos aquí.
                        Si tenés alguna duda, podés contactarnos en cualquier momento.
                    </p>
                </div>

                {/* 1. Datos de cuenta */}
                <div className="pp-section">
                    <div className="pp-section-header">
                        <div className="pp-section-icon icon-blue">👤</div>
                        <h2>1. Datos de cuenta y autenticación</h2>
                    </div>
                    <p>
                        Podés registrarte con tu correo electrónico o usando tu cuenta de Google
                        (Google Sign-In). Al hacerlo recopilamos:
                    </p>
                    <ul className="pp-data-list">
                        <li>Nombre de usuario o nombre de perfil de Google</li>
                        <li>Dirección de correo electrónico</li>
                        <li>Foto de perfil (si usás Google Sign-In)</li>
                        <li>Identificador único de usuario (UID generado por Firebase)</li>
                    </ul>
                    <div className="pp-divider" />
                    <p>
                        La autenticación es gestionada por <strong>Firebase Authentication</strong> (Google LLC).
                        Nunca almacenamos tu contraseña en texto plano.
                    </p>
                    <div className="pp-badges">
                        <span className="pp-badge">Firebase Authentication</span>
                        <span className="pp-badge">Google Sign-In</span>
                    </div>
                </div>

                {/* 2. Datos del juego */}
                <div className="pp-section">
                    <div className="pp-section-header">
                        <div className="pp-section-icon icon-green">🎮</div>
                        <h2>2. Datos de progreso y juego</h2>
                    </div>
                    <p>Para brindarte la experiencia de juego y mantener tu progreso, almacenamos:</p>
                    <ul className="pp-data-list">
                        <li>Puntuación, estadísticas y ranking</li>
                        <li>Saldo de monedas virtuales (coins)</li>
                        <li>Historial de partidas</li>
                        <li>Preferencias y configuración de la app</li>
                        <li>Estado de suscripciones activas</li>
                    </ul>
                    <div className="pp-divider" />
                    <p>
                        Estos datos se almacenan en <strong>Firebase Firestore</strong> y están vinculados a tu cuenta.
                        Podés solicitar la eliminación de tus datos en cualquier momento.
                    </p>
                    <div className="pp-badges">
                        <span className="pp-badge">Firebase Firestore</span>
                        <span className="pp-badge">Firebase Realtime Database</span>
                    </div>
                </div>

                {/* 3. Compras */}
                <div className="pp-section">
                    <div className="pp-section-header">
                        <div className="pp-section-icon icon-purple">💳</div>
                        <h2>3. Compras dentro de la app</h2>
                    </div>
                    <p>
                        Las compras (suscripciones y packs de monedas) son procesadas directamente por
                        <strong> Google Play</strong> o <strong>Apple App Store</strong> según tu dispositivo.
                        Footy Games no accede ni almacena los datos de tu tarjeta de crédito.
                    </p>
                    <p>
                        Usamos <strong>RevenueCat</strong> para gestionar y verificar el estado de las suscripciones.
                        RevenueCat recibe tu identificador de usuario y el historial de transacciones para
                        confirmar los beneficios activos.
                    </p>
                    <ul className="pp-data-list">
                        <li>Estado de suscripción (activa / vencida)</li>
                        <li>Historial de compras anónimizado</li>
                        <li>Identificador de transacción de la tienda</li>
                    </ul>
                    <div className="pp-badges">
                        <span className="pp-badge">RevenueCat</span>
                        <span className="pp-badge">Google Play Billing</span>
                        <span className="pp-badge">Apple StoreKit</span>
                    </div>
                </div>

                {/* 4. Publicidad */}
                <div className="pp-section">
                    <div className="pp-section-header">
                        <div className="pp-section-icon icon-orange">📢</div>
                        <h2>4. Publicidad (Google AdMob)</h2>
                    </div>
                    <p>
                        Los usuarios sin suscripción activa pueden ver anuncios provistos por
                        <strong> Google AdMob</strong>. Para mostrarte publicidad relevante, AdMob puede recopilar:
                    </p>
                    <ul className="pp-data-list">
                        <li>Identificador de publicidad del dispositivo (IDFA en iOS, GAID en Android)</li>
                        <li>Dirección IP y datos de ubicación aproximada</li>
                        <li>Información del dispositivo (modelo, sistema operativo, idioma)</li>
                        <li>Datos de interacción con anuncios (visualizaciones, clics)</li>
                    </ul>
                    <div className="pp-divider" />
                    <p>
                        Podés limitar el seguimiento publicitario desde la configuración de tu dispositivo:
                        en iOS en <strong>Ajustes → Privacidad → Seguimiento</strong>,
                        y en Android en <strong>Configuración → Google → Anuncios</strong>.
                        Los usuarios con suscripción <em>Sin Anuncios</em> o <em>Premium</em> no reciben publicidad.
                    </p>
                    <div className="pp-badges">
                        <span className="pp-badge">Google AdMob</span>
                        <span className="pp-badge">Google Ads SDK</span>
                    </div>
                </div>

                {/* 5. Analytics */}
                <div className="pp-section">
                    <div className="pp-section-header">
                        <div className="pp-section-icon icon-teal">📊</div>
                        <h2>5. Analíticas y rendimiento</h2>
                    </div>
                    <p>
                        Usamos <strong>Firebase Analytics</strong> para entender cómo los usuarios interactúan con
                        la app y así mejorar la experiencia. Los datos son anónimos y agregados:
                    </p>
                    <ul className="pp-data-list">
                        <li>Pantallas y funciones más visitadas</li>
                        <li>Duración de sesiones</li>
                        <li>Tipo de dispositivo y versión del sistema operativo</li>
                        <li>País o región (no ubicación exacta)</li>
                        <li>Eventos de juego (inicio de partida, fin de partida, etc.)</li>
                    </ul>
                    <div className="pp-badges">
                        <span className="pp-badge">Firebase Analytics</span>
                    </div>
                </div>

                {/* 6. Compartir datos */}
                <div className="pp-section">
                    <div className="pp-section-header">
                        <div className="pp-section-icon icon-yellow">🤝</div>
                        <h2>6. Compartir información con terceros</h2>
                    </div>
                    <p>No vendemos ni alquilamos tus datos personales. Solo los compartimos con:</p>
                    <ul className="pp-data-list">
                        <li><strong>Google LLC</strong> — Firebase (auth, base de datos, analytics) y AdMob (publicidad)</li>
                        <li><strong>RevenueCat Inc.</strong> — gestión de suscripciones y compras</li>
                        <li><strong>Apple Inc. / Google Inc.</strong> — procesamiento de pagos en tiendas</li>
                        <li>Autoridades legales, únicamente si la ley lo exige</li>
                    </ul>
                    <div className="pp-divider" />
                    <p>
                        Todos los terceros mencionados cuentan con sus propias políticas de privacidad y
                        cumplen con el RGPD y las leyes de privacidad aplicables.
                    </p>
                </div>

                {/* 7. Retención y eliminación */}
                <div className="pp-section">
                    <div className="pp-section-header">
                        <div className="pp-section-icon icon-red">🗑️</div>
                        <h2>7. Retención y eliminación de datos</h2>
                    </div>
                    <p>
                        Conservamos tus datos mientras tu cuenta esté activa. Podés solicitar la
                        eliminación completa de tu cuenta y datos asociados escribiéndonos a nuestro
                        correo. Procesamos las solicitudes en un plazo de 30 días.
                    </p>
                    <p>
                        Al desinstalar la app los datos locales del dispositivo se eliminan automáticamente,
                        pero los datos en la nube (Firestore) permanecen hasta que solicites la baja.
                    </p>
                </div>

                {/* 8. Derechos */}
                <div className="pp-section">
                    <div className="pp-section-header">
                        <div className="pp-section-icon icon-blue">⚖️</div>
                        <h2>8. Tus derechos</h2>
                    </div>
                    <p>Tenés derecho a:</p>
                    <ul className="pp-data-list">
                        <li>Acceder a los datos personales que tenemos sobre vos</li>
                        <li>Corregir datos inexactos</li>
                        <li>Solicitar la eliminación de tu cuenta y datos</li>
                        <li>Oponerte al uso de tus datos para publicidad personalizada</li>
                        <li>Portar tus datos a otro servicio</li>
                    </ul>
                    <div className="pp-divider" />
                    <p>
                        Para ejercer cualquiera de estos derechos simplemente escribinos al correo indicado
                        al final de esta página.
                    </p>
                </div>

                {/* 9. Cambios */}
                <div className="pp-section">
                    <div className="pp-section-header">
                        <div className="pp-section-icon icon-green">🔄</div>
                        <h2>9. Cambios en esta política</h2>
                    </div>
                    <p>
                        Podemos actualizar esta política ocasionalmente. Cuando lo hagamos, actualizaremos
                        la fecha de "última actualización" al inicio de esta página. Te recomendamos
                        revisarla periódicamente. El uso continuado de la app tras los cambios implica
                        la aceptación de la nueva política.
                    </p>
                </div>

                {/* Contacto */}
                <div className="pp-contact">
                    <h2>¿Tenés alguna pregunta?</h2>
                    <p>
                        Si querés ejercer tus derechos, solicitar la eliminación de tus datos
                        o tenés cualquier consulta sobre esta política, contactanos:
                    </p>
                    <a href="mailto:fty.tictactoe@gmail.com">fty.tictactoe@gmail.com</a>
                </div>

                <p className="pp-footer-note">
                    © {new Date().getFullYear()} Footy Games · Todos los derechos reservados
                </p>

            </div>
        </div>
    );
};

export default PoliticaPrivacidad;
